/**
 * Per-card refinement panel — cycle-spend and milestone-progress questions
 * scoped to this single card. Pure per-card state; never touches anything
 * else in the wallet.
 *
 * Conservative defaults: the unset state is always the LOWEST-reward bucket,
 * so the user only ever INCREASES the displayed reward by answering.
 */
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const ChipRow = ({ options, value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
    {options.map((opt) => {
      const selected = value === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(selected ? null : opt.id)}
          className={cn(
            'inline-flex items-center px-3 py-1.5 rounded-full text-xs border transition-colors',
            selected
              ? 'bg-accent text-accent-foreground border-accent'
              : 'bg-transparent text-foreground/80 border-border hover:bg-muted hover:text-foreground'
          )}
          data-testid={`${testIdPrefix}-chip-${opt.id}`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

export const RefinementPanel = ({ schema, answers, onChange, isLoading }) => {
  const questions = schema?.questions || [];
  if (!questions.length) return null;
  const a = answers || {};

  const updateCycleSpend = (bucketId, assumedInr) => {
    onChange({ ...a, cycle_spend_bucket: bucketId, cycle_spend_inr: bucketId == null ? null : assumedInr });
  };
  const updateMilestone = (bucketId, assumedInr) => {
    onChange({ ...a, milestone_bucket: bucketId, milestone_progress_inr: bucketId == null ? null : assumedInr });
  };

  return (
    <div
      className={cn('space-y-4', isLoading && 'opacity-60 pointer-events-none')}
      data-testid="refinement-panel"
    >
      {questions.map((q, idx) => {
        if (q.kind === 'cycle_spend') {
          const opts = q.buckets.map((b) => ({ id: b.id, label: b.label }));
          const cur = a.cycle_spend_bucket || 'zero';
          return (
            <div key={idx} data-testid={`refinement-q-${idx}`}>
              <Label className="text-xs font-medium text-foreground mb-2 block">
                This statement cycle spend on this card?
                <span className="text-[10px] uppercase text-muted-foreground ml-2 tracking-wider">
                  affects “{q.rule_label}”
                </span>
              </Label>
              <ChipRow
                options={opts}
                value={cur}
                onChange={(id) => {
                  if (!id) return updateCycleSpend(null, null);
                  const b = q.buckets.find((x) => x.id === id);
                  updateCycleSpend(id, b?.assumed_used_inr || 0);
                }}
                testIdPrefix={`refinement-q-${idx}-cycle`}
              />
            </div>
          );
        }
        if (q.kind === 'milestone_progress') {
          const opts = q.buckets.map((b) => ({ id: b.id, label: b.label }));
          const cur = a.milestone_bucket || 'below_lowest';
          return (
            <div key={idx} data-testid={`refinement-q-${idx}`}>
              <Label className="text-xs font-medium text-foreground mb-2 block">
                Anniversary cycle spend so far on this card?
                <span className="text-[10px] uppercase text-muted-foreground ml-2 tracking-wider">
                  {q.milestone_count} milestone{q.milestone_count === 1 ? '' : 's'}
                </span>
              </Label>
              <ChipRow
                options={opts}
                value={cur}
                onChange={(id) => {
                  if (!id) return updateMilestone(null, null);
                  const b = q.buckets.find((x) => x.id === id);
                  updateMilestone(id, b?.assumed_progress_inr || 0);
                }}
                testIdPrefix={`refinement-q-${idx}-milestone`}
              />
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};
